/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  CircularProgress,
  Typography,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableFooter,
  Card,
  CardActionArea,
  CardContent,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  PurchaseOrdersProvider,
  usePurchaseOrders,
} from "../../contexts/albina/PurchaseOrdersContext";

import {
  GetApp as DownloadIcon,
  Print as PrintIcon,
} from "@mui/icons-material";

import STORE_NAMES from "../../utils/STORE_NAMES";

// 📦 Ajout des libs pour export
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Upload } from "@mui/icons-material";

// =====================
// Styles & Animations
// =====================
const bgGradient =
  "linear-gradient(135deg, rgba(14,165,233,0.06), rgba(99,102,241,0.06))";

const floatIn = keyframes`
  0% { transform: translateY(8px); opacity: 0; }
  60% { transform: translateY(-6px); opacity: 1; }
  100% { transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Root = styled("div")(({ theme }) => ({
  minHeight: "100%",
  padding: theme.spacing(3),
  background:
    "radial-gradient(circle at 10% 10%, rgba(6,182,212,0.08), transparent 10%), radial-gradient(circle at 90% 90%, rgba(99,102,241,0.06), transparent 10%), linear-gradient(180deg, rgba(246,248,255,0.6), rgba(250,250,255,0.94))",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const HeaderBar = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
}));

const Title = styled("h2")(({ theme }) => ({
  margin: 0,
  fontSize: "1.6rem",
  letterSpacing: "-0.2px",
  background: "linear-gradient(90deg, #06b6d4, #6366f1, #4f46e5)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundSize: "200% 100%",
  animation: `${shimmer} 3s linear infinite`,
  fontWeight: 700,
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const StoreGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  flexWrap: "wrap",
}));

const StoreCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  width: 220,
  minWidth: 180,
  borderRadius: 14,
  overflow: "visible",
  position: "relative",
  transition: "transform 300ms ease, box-shadow 300ms ease",
  transform: active ? "translateY(-6px) scale(1.02)" : "none",
  boxShadow: active
    ? "0 10px 30px rgba(15,23,42,0.18)"
    : "0 6px 18px rgba(2,6,23,0.06)",
  background: active
    ? "linear-gradient(180deg, rgba(255,255,255,0.9), rgba(249,250,255,0.76))"
    : bgGradient,
  border: active
    ? "1px solid rgba(99,102,241,0.18)"
    : "1px solid rgba(99,102,241,0.06)",
  "&:hover": {
    transform: "translateY(-6px) scale(1.02)",
    boxShadow: "0 12px 30px rgba(79,70,229,0.12)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: -2,
    borderRadius: 16,
    background:
      "linear-gradient(90deg, rgba(6,182,212,0.08), rgba(99,102,241,0.08))",
    zIndex: -1,
  },
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(250,250,255,0.5))",
  boxShadow: "0 8px 30px rgba(2,6,23,0.06)",
  backdropFilter: "blur(6px)",
  border: "1px solid rgba(99,102,241,0.06)",
  overflow: "hidden",
}));

const GradientButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  borderRadius: 10,
  padding: theme.spacing(1, 2),
  fontWeight: 600,
  boxShadow: "0 8px 20px rgba(99,102,241,0.08)",
  background: "linear-gradient(90deg, #06b6d4 0%, #6366f1 100%)",
  color: "#fff",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 30px rgba(99,102,241,0.14)",
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  borderRadius: 10,
  padding: theme.spacing(0.8, 1.6),
  fontWeight: 600,
  border: "1px solid rgba(99,102,241,0.12)",
  background: "transparent",
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  padding: theme.spacing(2),
  fontSize: 14,
  background:
    "linear-gradient(90deg, rgba(14,165,233,0.06), rgba(99,102,241,0.06))",
}));

const baseUrl = process.env.REACT_APP_BASE_URL;

// =====================
// Component (same logic, only visual changes)
// =====================
const PurchaseOrdersTable = () => {
  const endpoint0 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_0;
  const endpoint1 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_1;
  const endpoint2 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_2;
  const endpoint3 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_3;
  const endpoint4 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_4;
  const endpoint5 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_5;
  const endpoint6 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_6;
  const endpoint7 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_7;

  const [selectedStores, setSelectedStores] = useState([]);

  const {
    purchaseOrders,
    purchaseOrders1,
    purchaseOrders2,
    purchaseOrders3,
    purchaseOrders4,
    purchaseOrders5,
    purchaseOrders6,
    purchaseOrders7,
    suppliers,
    total,
    total1,
    total2,
    total3,
    total4,
    total5,
    total6,
    total7,
    loading,
    error,
    goods,
    categories,
  } = usePurchaseOrders();

  const [searchTerm0, setSearchTerm0] = useState("");
  const [searchTerm1, setSearchTerm1] = useState("");
  const [searchTerm2, setSearchTerm2] = useState("");
  const [searchTerm3, setSearchTerm3] = useState("");
  const [searchTerm4, setSearchTerm4] = useState("");
  const [searchTerm5, setSearchTerm5] = useState("");
  const [searchTerm6, setSearchTerm6] = useState("");
  const [searchTerm7, setSearchTerm7] = useState("");

  const [filterFrom3, setFilterFrom3] = useState("");
  const [filterTo3, setFilterTo3] = useState("");
  const [filterFrom4, setFilterFrom4] = useState("");
  const [filterTo4, setFilterTo4] = useState("");
  const [filterFrom5, setFilterFrom5] = useState("");
  const [filterTo5, setFilterTo5] = useState("");
  const [filterFrom6, setFilterFrom6] = useState("");
  const [filterTo6, setFilterTo6] = useState("");

  const [filterFrom7, setFilterFrom7] = useState("");
  const [filterTo7, setFilterTo7] = useState("");

  const [filterFrom0, setFilterFrom0] = useState("");
  const [filterTo0, setFilterTo0] = useState("");
  const [filterFrom1, setFilterFrom1] = useState("");
  const [filterTo1, setFilterTo1] = useState("");
  const [filterFrom2, setFilterFrom2] = useState("");
  const [filterTo2, setFilterTo2] = useState("");

  const [showTable0, setShowTable0] = useState(false);
  const [showTable1, setShowTable1] = useState(false);
  const [showTable2, setShowTable2] = useState(false);

  const [showTable3, setShowTable3] = useState(false);
  const [showTable4, setShowTable4] = useState(false);
  const [showTable5, setShowTable5] = useState(false);
  const [showTable6, setShowTable6] = useState(false);
  const [showTable7, setShowTable7] = useState(false);

  const [page0, setPage0] = useState(0);
  const [page1, setPage1] = useState(0);
  const [page2, setPage2] = useState(0);
  const [page3, setPage3] = useState(0);
  const [page4, setPage4] = useState(0);
  const [page5, setPage5] = useState(0);
  const [page6, setPage6] = useState(0);
  const [page7, setPage7] = useState(0);

  const [rowsPerPage0, setRowsPerPage0] = useState(10);
  const [rowsPerPage1, setRowsPerPage1] = useState(10);
  const [rowsPerPage2, setRowsPerPage2] = useState(10);
  const [rowsPerPage3, setRowsPerPage3] = useState(10);
  const [rowsPerPage4, setRowsPerPage4] = useState(10);
  const [rowsPerPage5, setRowsPerPage5] = useState(10);
  const [rowsPerPage6, setRowsPerPage6] = useState(10);
  const [rowsPerPage7, setRowsPerPage7] = useState(10);

  const [supplierFilter, setSupplierFilter] = useState("");

  const [globalGoodFilter, setGlobalGoodFilter] = useState("");
  const [globalUnitFilter, setGlobalUnitFilter] = useState("");

  const [supplierFilter0, setSupplierFilter0] = useState("");
  const [supplierFilter1, setSupplierFilter1] = useState("");
  const [supplierFilter2, setSupplierFilter2] = useState("");
  const [supplierFilter3, setSupplierFilter3] = useState("");
  const [supplierFilter4, setSupplierFilter4] = useState("");
  const [supplierFilter5, setSupplierFilter5] = useState("");
  const [supplierFilter6, setSupplierFilter6] = useState("");
  const [supplierFilter7, setSupplierFilter7] = useState("");

  const [goodFilter0, setGoodFilter0] = useState("");
  const [goodFilter1, setGoodFilter1] = useState("");
  const [goodFilter2, setGoodFilter2] = useState("");
  const [goodFilter3, setGoodFilter3] = useState("");
  const [goodFilter4, setGoodFilter4] = useState("");
  const [goodFilter5, setGoodFilter5] = useState("");
  const [goodFilter6, setGoodFilter6] = useState("");
  const [goodFilter7, setGoodFilter7] = useState("");

  const [unitFilter0, setUnitFilter0] = useState("");
  const [unitFilter1, setUnitFilter1] = useState("");
  const [unitFilter2, setUnitFilter2] = useState("");
  const [unitFilter3, setUnitFilter3] = useState("");
  const [unitFilter4, setUnitFilter4] = useState("");
  const [unitFilter5, setUnitFilter5] = useState("");
  const [unitFilter6, setUnitFilter6] = useState("");
  const [unitFilter7, setUnitFilter7] = useState("");

  const [openModal, setOpenModal] = useState(false);

  const [fetchedPO, setFetchedPO] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const loginAndGetToken = async () => {
    const loginUrl = "https://dimspos.com/api/login";
    const credentials = {
      email: "mgmt@dimsapp.com",
      password: "rC2v#rptdwTuk",
    };

    const response = await axios.post(loginUrl, credentials);

    if (!response.data?.token) {
      console.error("Réponse login:", response.data);
      throw new Error("Impossible de récupérer le token.");
    }

    return response.data.token;
  };

  const getStoreFromUrl = (url) => {
    try {
      const params = new URL(url).searchParams;
      return params.get("0_store");
    } catch (err) {
      console.error("Invalid URL:", err);
      return null;
    }
  };

  const storeId0 = getStoreFromUrl(endpoint0);
  const storeId1 = getStoreFromUrl(endpoint1);
  const storeId2 = getStoreFromUrl(endpoint2);
  const storeId3 = getStoreFromUrl(endpoint3);
  const storeId4 = getStoreFromUrl(endpoint4);
  const storeId5 = getStoreFromUrl(endpoint5);
  const storeId6 = getStoreFromUrl(endpoint6);
  const storeId7 = getStoreFromUrl(endpoint7);

  const storeName0 = STORE_NAMES[storeId0] || "Unknown Store";
  const storeName1 = STORE_NAMES[storeId1] || "Unknown Store";
  const storeName2 = STORE_NAMES[storeId2] || "Unknown Store";
  const storeName3 = STORE_NAMES[storeId3] || "Unknown Store";
  const storeName4 = STORE_NAMES[storeId4] || "Unknown Store";
  const storeName5 = STORE_NAMES[storeId5] || "Unknown Store";
  const storeName6 = STORE_NAMES[storeId6] || "Unknown Store";
  const storeName7 = STORE_NAMES[storeId7] || "Unknown Store";

  // ---- FETCH PO DETAILS ----

  const fetchById = async (id, storeId) => {
    if (!id) {
      console.error("No ID found for PO details fetch");
      setFetchedPO(null);
      setModalLoading(false);
      return;
    }

    if (!storeId) {
      console.error("No storeId provided for PO details fetch");
      setFetchedPO(null);
      setModalLoading(false);
      return;
    }

    try {
      setModalLoading(true);
      setOpenModal(true);

      // 1. Récupérer le token
      let token;
      try {
        token = await loginAndGetToken();
      } catch (loginError) {
        console.error("Error during login:", loginError);
        setFetchedPO(null);
        setModalLoading(false);
        return;
      }

      // 2. Construire l'URL de l'API Dimspos
      const url = `https://dimspos.com/api/654499273f4c01073c004a29/${storeId}/model/store_purchase_orders/id/${id}/received_purchase_orders_details`;
      console.log("Constructed URL:", url);

      // 3. Faire l'appel avec le token
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Fetched PO details:", response.data);
      setFetchedPO(response?.data?.item);
    } catch (error) {
      console.error("Erreur lors du fetch PO details:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
      });
      setFetchedPO(null);
    } finally {
      setModalLoading(false);
    }
  };

  //useEffect(()=>{fetchById()},[]);

  //useEffect(()=>{},[fetchedPO])

  // ---- LOGIQUE DES CARDS ----
  const toggleStore = (storeIndex) => {
    if (selectedStores.includes(storeIndex)) {
      setSelectedStores(selectedStores.filter((s) => s !== storeIndex));
      if (storeIndex === 0) setShowTable0(false);
      if (storeIndex === 1) setShowTable1(false);
      if (storeIndex === 2) setShowTable2(false);
      if (storeIndex === 3) setShowTable3(false);
      if (storeIndex === 4) setShowTable4(false);
      if (storeIndex === 5) setShowTable5(false);
      if (storeIndex === 6) setShowTable6(false);
      if (storeIndex === 7) setShowTable7(false);
    } else {
      setSelectedStores([...selectedStores, storeIndex]);
      if (storeIndex === 0) setShowTable0(true);
      if (storeIndex === 1) setShowTable1(true);
      if (storeIndex === 2) setShowTable2(true);
      if (storeIndex === 3) setShowTable3(true);
      if (storeIndex === 4) setShowTable4(true);
      if (storeIndex === 5) setShowTable5(true);
      if (storeIndex === 6) setShowTable6(true);
      if (storeIndex === 7) setShowTable7(true);
    }
  };

  // ---- FILTRE PAR PLAGE DE DATES ----
  const filterOrdersByDateRange = (
    orders,
    search,
    from,
    to,
    localSupplier,
    globalSupplier,
    goodFilter,
    unitFilter,
    globalGood,
    globalUnit
  ) => {
    if (!orders) return [];

    return orders.filter((po) => {
      // 🔍 Search
      const matchSearch = Object.values(po).some((value) =>
        value?.toString().toLowerCase().includes(search.toLowerCase())
      );

      // 📅 Dates
      let matchDate = true;
      const poDate = po.received_date ? new Date(po.received_date) : null;
      const fromDate = from ? new Date(from) : null;
      const toDate = to ? new Date(to) : null;

      if (poDate && fromDate && toDate) {
        matchDate = poDate >= fromDate && poDate <= toDate;
      } else if (poDate && fromDate) {
        matchDate = poDate >= fromDate;
      } else if (poDate && toDate) {
        matchDate = poDate <= toDate;
      }

      // 🏭 Local supplier filter
      let matchLocalSupplier = true;
      if (localSupplier) {
        matchLocalSupplier = po.po_inv_items?.some(
          (item) => item.supplier === localSupplier
        );
      }

      // 🌍 Global supplier filter
      let matchGlobalSupplier = true;
      if (globalSupplier) {
        matchGlobalSupplier = po.po_inv_items?.some(
          (item) => item.supplier === globalSupplier
        );
      }

      // 📦 Good filter
      let matchGood = true;
      if (goodFilter) {
        matchGood = po.po_inv_items?.some((item) => item.good === goodFilter);
      }

      // 📏 Unit filter
      let matchUnit = true;
      if (unitFilter && goodFilter) {
        matchUnit = po.po_inv_items?.some((item) => {
          if (item.good !== goodFilter) return false;
          const good = goods.find((g) => g._id === item.good);
          return good?.measurement_unit === unitFilter;
        });
      }

      // 🌍 Global good filter
      let matchGlobalGood = true;
      if (globalGood) {
        matchGlobalGood = po.po_inv_items?.some(
          (item) => item.good === globalGood
        );
      }

      // 🌍 Global unit filter
      let matchGlobalUnit = true;
      if (globalUnit && globalGood) {
        matchGlobalUnit = po.po_inv_items?.some((item) => {
          if (item.good !== globalGood) return false;
          const good = goods.find((g) => g._id === item.good);
          return good?.measurement_unit === globalUnit;
        });
      }

      return (
        matchSearch &&
        matchDate &&
        matchLocalSupplier &&
        matchGlobalSupplier &&
        matchGood &&
        matchUnit &&
        matchGlobalGood &&
        matchGlobalUnit
      );
    });
  };

  const filteredPurchaseOrders0 = useMemo(
    () =>
      filterOrdersByDateRange(
        purchaseOrders,
        searchTerm0,
        filterFrom0,
        filterTo0,
        supplierFilter0,
        supplierFilter,
        goodFilter0,
        unitFilter0,
        globalGoodFilter,
        globalUnitFilter
      ),
    [
      purchaseOrders,
      searchTerm0,
      filterFrom0,
      filterTo0,
      supplierFilter0,
      supplierFilter,
      goodFilter0,
      unitFilter0,
      globalGoodFilter,
      globalUnitFilter,
      goods,
    ]
  );

  const filteredPurchaseOrders1 = useMemo(
    () =>
      filterOrdersByDateRange(
        purchaseOrders1,
        searchTerm1,
        filterFrom1,
        filterTo1,
        supplierFilter1,
        supplierFilter,
        goodFilter1,
        unitFilter1,
        globalGoodFilter,
        globalUnitFilter
      ),
    [
      purchaseOrders1,
      searchTerm1,
      filterFrom1,
      filterTo1,
      supplierFilter1,
      supplierFilter,
      goodFilter1,
      unitFilter1,
      globalGoodFilter,
      globalUnitFilter,
      goods,
    ]
  );

  const filteredPurchaseOrders2 = useMemo(
    () =>
      filterOrdersByDateRange(
        purchaseOrders2,
        searchTerm2,
        filterFrom2,
        filterTo2,
        supplierFilter2,
        supplierFilter,
        goodFilter2,
        unitFilter2,
        globalGoodFilter,
        globalUnitFilter
      ),
    [
      purchaseOrders2,
      searchTerm2,
      filterFrom2,
      filterTo2,
      supplierFilter2,
      supplierFilter,
      goodFilter2,
      unitFilter2,
      globalGoodFilter,
      globalUnitFilter,
      goods,
    ]
  );

  const filteredPurchaseOrders3 = useMemo(
    () =>
      filterOrdersByDateRange(
        purchaseOrders3,
        searchTerm3,
        filterFrom3,
        filterTo3,
        supplierFilter3,
        supplierFilter,
        goodFilter3,
        unitFilter3,
        globalGoodFilter,
        globalUnitFilter
      ),
    [
      purchaseOrders3,
      searchTerm3,
      filterFrom3,
      filterTo3,
      supplierFilter3,
      supplierFilter,
      goodFilter3,
      unitFilter3,
      globalGoodFilter,
      globalUnitFilter,
      goods,
    ]
  );
  const filteredPurchaseOrders4 = useMemo(
    () =>
      filterOrdersByDateRange(
        purchaseOrders4,
        searchTerm4,
        filterFrom4,
        filterTo4,
        supplierFilter4,
        supplierFilter,
        goodFilter4,
        unitFilter4,
        globalGoodFilter,
        globalUnitFilter
      ),
    [
      purchaseOrders4,
      searchTerm4,
      filterFrom4,
      filterTo4,
      supplierFilter4,
      supplierFilter,
      goodFilter4,
      unitFilter4,
      globalGoodFilter,
      globalUnitFilter,
      goods,
    ]
  );

  const filteredPurchaseOrders5 = useMemo(
    () =>
      filterOrdersByDateRange(
        purchaseOrders5,
        searchTerm5,
        filterFrom5,
        filterTo5,
        supplierFilter5,
        supplierFilter,
        goodFilter5,
        unitFilter5,
        globalGoodFilter,
        globalUnitFilter
      ),
    [
      purchaseOrders5,
      searchTerm5,
      filterFrom5,
      filterTo5,
      supplierFilter5,
      supplierFilter,
      goodFilter5,
      unitFilter5,
      globalGoodFilter,
      globalUnitFilter,
      goods,
    ]
  );

  const filteredPurchaseOrders6 = useMemo(
    () =>
      filterOrdersByDateRange(
        purchaseOrders6,
        searchTerm6,
        filterFrom6,
        filterTo6,
        supplierFilter6,
        supplierFilter,
        goodFilter6,
        unitFilter6,
        globalGoodFilter,
        globalUnitFilter
      ),
    [
      purchaseOrders6,
      searchTerm6,
      filterFrom6,
      filterTo6,
      supplierFilter6,
      supplierFilter,
      goodFilter6,
      unitFilter6,
      globalGoodFilter,
      globalUnitFilter,
      goods,
    ]
  );

  const filteredPurchaseOrders7 = useMemo(
    () =>
      filterOrdersByDateRange(
        purchaseOrders7,
        searchTerm7,
        filterFrom7,
        filterTo7,
        supplierFilter7,
        supplierFilter,
        goodFilter7,
        unitFilter7,
        globalGoodFilter,
        globalUnitFilter
      ),
    [
      purchaseOrders7,
      searchTerm7,
      filterFrom7,
      filterTo7,
      supplierFilter7,
      supplierFilter,
      goodFilter7,
      unitFilter7,
      globalGoodFilter,
      globalUnitFilter,
      goods,
    ]
  );

  // ---- TOTAL FILTRÉ ----
  const calculateFilteredTotal = (orders) => {
    return orders.reduce((acc, po) => acc + (po.total || po.amount || 0), 0);
  };

  const filteredTotal0 = useMemo(
    () => calculateFilteredTotal(filteredPurchaseOrders0),
    [filteredPurchaseOrders0]
  );
  const filteredTotal1 = useMemo(
    () => calculateFilteredTotal(filteredPurchaseOrders1),
    [filteredPurchaseOrders1]
  );
  const filteredTotal2 = useMemo(
    () => calculateFilteredTotal(filteredPurchaseOrders2),
    [filteredPurchaseOrders2]
  );

  const filteredTotal3 = useMemo(
    () => calculateFilteredTotal(filteredPurchaseOrders3),
    [filteredPurchaseOrders3]
  );

  const filteredTotal4 = useMemo(
    () => calculateFilteredTotal(filteredPurchaseOrders4),
    [filteredPurchaseOrders4]
  );

  const filteredTotal5 = useMemo(
    () => calculateFilteredTotal(filteredPurchaseOrders5),
    [filteredPurchaseOrders5]
  );

  const filteredTotal6 = useMemo(
    () => calculateFilteredTotal(filteredPurchaseOrders6),
    [filteredPurchaseOrders6]
  );

  const filteredTotal7 = useMemo(
    () => calculateFilteredTotal(filteredPurchaseOrders7),
    [filteredPurchaseOrders7]
  );

  // ---- EXPORT EXCEL ----
  const exportToExcel = (orders, storeName) => {
    const data = orders.map((po, index) => {
      const supplierId = po.po_inv_items?.find(
        (item) => item.supplier
      )?.supplier;
      const supplierName =
        suppliers.find((s) => s._id === supplierId)?.name || "N/A";
      return {
        Ref: `#${po._id ? po._id.slice(0, 8) : po.id || index}`,
        Supplier: supplierName,
        Date: po.received_date || po.orderDate || "N/A",
        Total: po.total || po.amount?.toFixed(2) || "N/A",
        Comments: po.comments || "N/A",
      };
    });

    const totalAmount = orders.reduce(
      (acc, po) => acc + (po.total || po.amount || 0),
      0
    );
    const totalRow = {
      Ref: "Total",
      Supplier: "",
      Date: "",
      Total: totalAmount,
      Comments: "",
    };
    data.push(totalRow);

    const ws = XLSX.utils.json_to_sheet(data);

    // Apply bold style to the total row
    const totalRowIndex = orders.length + 2; // Row number for total row
    ws["A" + totalRowIndex].s = { font: { bold: true } };
    ws["B" + totalRowIndex].s = { font: { bold: true } };
    ws["C" + totalRowIndex].s = { font: { bold: true } };
    ws["D" + totalRowIndex].s = { font: { bold: true } };
    ws["E" + totalRowIndex].s = { font: { bold: true } };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(
      wb,
      `PurchaseOrders_${storeName}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
  };

  const exportToPDF = (orders, storeName, suppliers = []) => {
    const doc = new jsPDF();

    // ===== STYLES =====
    const now = new Date();
    const formattedDate = now.toLocaleDateString("en-GB"); // dd/mm/yyyy

    // ===== HEADER =====
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Purchase Orders Report", 10, 15);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Store: ${storeName}`, 10, 25);
    doc.text(`Report Date: ${formattedDate}`, 10, 32);

    // Optional: Print total POs and total amount
    const totalAmount = orders.reduce(
      (acc, po) => acc + (po.total || po.amount || 0),
      0
    );
    doc.text(`Total Orders: ${orders.length}`, 10, 39);
    doc.text(`Total Amount: ${totalAmount.toFixed(2)} AED`, 10, 46);

    // ===== TABLE HEADERS =====
    const tableHead = [["Ref", "Supplier", "Date", "Total (AED)", "Comments"]];

    // ===== TABLE BODY =====
    const tableBody = orders.map((po, index) => {
      const supplierId = po.po_inv_items?.find(
        (item) => item.supplier
      )?.supplier;
      const supplierName =
        suppliers.find((s) => s._id === supplierId)?.name || "N/A";

      return [
        `#${po._id ? po._id.slice(0, 8) : po.id || index}`,
        supplierName,
        po.received_date || po.orderDate || "N/A",
        (po.total || po.amount || 0).toFixed(2),
        po.comments || "N/A",
      ];
    });

    // ===== TABLE RENDER =====
    autoTable(doc, {
      head: tableHead,
      body: tableBody,
      startY: 55,
      theme: "grid",
      headStyles: {
        fillColor: [41, 128, 185], // nice blue
        textColor: 255,
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: 50,
        fontSize: 10,
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
      styles: {
        cellPadding: 3,
      },
      didDrawPage: (data) => {
        // ===== FOOTER with page number =====
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height
          ? pageSize.height
          : pageSize.getHeight();
        const pageNum = doc.internal.getNumberOfPages();

        doc.setFontSize(9);
        doc.setTextColor(150);
        doc.text(`Page ${pageNum}`, pageSize.width - 20, pageHeight - 10);
      },
    });

    // ===== SAVE FILE =====
    const filename = `PurchaseOrders_${storeName}_${
      now.toISOString().split("T")[0]
    }.pdf`;
    doc.save(filename);
  };

  // ---- GET STORE NAME FOR PO ----
  const getStoreNameForPO = (po) => {
    // Check which store's data contains this PO
    if (purchaseOrders.some((p) => p._id === po._id)) return storeName0;
    if (purchaseOrders1.some((p) => p._id === po._id)) return storeName1;
    if (purchaseOrders2.some((p) => p._id === po._id)) return storeName2;
    return "Unknown Store"; // fallback
  };

  // ---- GENERATE INDIVIDUAL INVOICE PDF ----
  const generateInvoicePDF = (po, storeName) => {
    const doc = new jsPDF();

    // Company Header
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Dimensions Plus", 20, 25);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Restaurant Inventory Management System", 20, 35);
    doc.text("Store: " + storeName, 20, 45);

    // Invoice Title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("PURCHASE ORDER INVOICE", 20, 65);

    // Purchase Order Details
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const poRef = po._id ? po._id.slice(0, 8) : po.id || "N/A";
    const poDate = po.received_date || po.orderDate || "N/A";
    const supplierInfo = suppliers.find(
      (s) => s._id === po.po_inv_items?.[0]?.supplier
    );

    doc.text(`PO Reference: #${poRef}`, 20, 80);
    doc.text(`Date: ${poDate}`, 20, 90);
    doc.text(`Supplier: ${supplierInfo?.name || "N/A"}`, 20, 100);
    doc.text(`Comments: ${po.comments || "N/A"}`, 20, 110);

    // Line separator
    doc.line(20, 120, 190, 120);

    // Items Table
    const itemsData = goods
      .filter((good) => po.po_inv_items?.some((inv) => inv.good === good._id))
      .map((good, index) => {
        const poItem = po.po_inv_items.find((inv) => inv.good === good._id);
        const qty = Number(poItem?.quantity) || 0;
        const goodSupplier = good.goods_suppliers?.[index] || {};
        const price = Number(goodSupplier.price) || 0;
        const taxRate = Number(goodSupplier.tax_rate) || 0;
        const lineTotal = price * qty;
        const taxAmount = lineTotal * (taxRate / 100);
        const totalWithTax = lineTotal + taxAmount;

        return [
          good.name || "N/A",
          qty.toString(),
          good.measurement_unit || "N/A",
          `AED ${price.toFixed(2)}`,
          `AED ${lineTotal.toFixed(2)}`,
          `${taxRate}%`,
          `AED ${taxAmount.toFixed(2)}`,
          `AED ${totalWithTax.toFixed(2)}`,
        ];
      });

    // Calculate totals
    const totals = goods
      .filter((good) => po.po_inv_items?.some((inv) => inv.good === good._id))
      .reduce(
        (acc, good) => {
          const poItem = po.po_inv_items.find((inv) => inv.good === good._id);
          const qty = Number(poItem?.quantity) || 0;
          const goodSupplier = good.goods_suppliers?.[0] || {};
          const price = Number(goodSupplier.price) || 0;
          const taxRate = Number(goodSupplier.tax_rate) || 0;
          const lineTotal = price * qty;
          const taxAmount = lineTotal * (taxRate / 100);

          acc.subtotal += lineTotal;
          acc.totalTax += taxAmount;
          return acc;
        },
        { subtotal: 0, totalTax: 0 }
      );

    autoTable(doc, {
      head: [
        [
          "Item",
          "Qty",
          "Unit",
          "Price",
          "Subtotal",
          "Tax Rate",
          "Tax Amount",
          "Total",
        ],
      ],
      body: itemsData,
      startY: 130,
      theme: "grid",
      headStyles: {
        fillColor: [99, 102, 241],
        textColor: 255,
        fontStyle: "bold",
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "center" },
        6: { halign: "right" },
        7: { halign: "right", fontStyle: "bold" },
      },
    });

    // Add totals section
    const finalY = doc.lastAutoTable.finalY + 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Subtotal: AED ${totals.subtotal.toFixed(2)}`, 140, finalY);
    doc.text(`Total Tax: AED ${totals.totalTax.toFixed(2)}`, 140, finalY + 10);
    doc.text(
      `Grand Total: AED ${(totals.subtotal + totals.totalTax).toFixed(2)}`,
      140,
      finalY + 20
    );

    // Footer
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Generated by DIMS Plus System", 20, finalY + 40);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, finalY + 50);

    // Save the PDF
    doc.save(
      `Invoice_${poRef}_${storeName}_${
        new Date().toISOString().split("T")[0]
      }.pdf`
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="280px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="h6">
        Error: {error}
      </Typography>
    );
  }

  const handleChangePage = (setter) => (event, newPage) => setter(newPage);
  const handleChangeRowsPerPage = (setter, pageSetter) => (event) => {
    setter(parseInt(event.target.value, 10));
    pageSetter(0);
  };

  // ---- RENDER TABLE ----
  const renderTable = (
    storeName,
    total,
    filteredOrders,
    filteredTotal,
    showTable,
    setShowTable,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchTerm,
    setSearchTerm,
    filterFrom,
    setFilterFrom,
    filterTo,
    setFilterTo,
    localSupplierFilter,
    setLocalSupplierFilter,
    goodFilter,
    setGoodFilter,
    unitFilter,
    setUnitFilter,
    tableIndex,
    globalGoodFilter,
    globalUnitFilter
  ) => (
    <div>
      {!showTable ? null : (
        <Box mb={1} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {storeName}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Total Global: AED {total?.toFixed(2)}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Total Filtered: AED {filteredTotal?.toFixed(2)}
          </Typography>
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Box display="flex" gap={1}>
          <SecondaryButton
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            disabled={!showTable}
            onClick={() => exportToExcel(filteredOrders, storeName)}
          >
            Export Excel
          </SecondaryButton>
          <SecondaryButton
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            disabled={!showTable}
            onClick={() => exportToPDF(filteredOrders, storeName)}
          >
            Export PDF
          </SecondaryButton>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <SubTitle variant="caption">
            {showTable ? "Table active" : "Table inactive"}
          </SubTitle>
          <StyledChip
            label={showTable ? "Selected" : "Not selected"}
            color="info"
          />
        </Box>
      </Box>

      {showTable ? (
        <>
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              sx={{ minWidth: 220 }}
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <TextField
              label="From"
              variant="outlined"
              size="small"
              type="date"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="To"
              variant="outlined"
              size="small"
              type="date"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="Local Supplier Filter"
              select
              size="small"
              value={localSupplierFilter}
              onChange={(e) => setLocalSupplierFilter(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All Local Suppliers</MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Good Filter"
              select
              size="small"
              value={goodFilter}
              onChange={(e) => {
                setGoodFilter(e.target.value);
                setUnitFilter(""); // reset unit when good changes
              }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All Goods</MenuItem>
              {goods.map((g) => (
                <MenuItem key={g._id} value={g._id}>
                  {g.name} ({g.measurement_unit || "N/A"})
                </MenuItem>
              ))}
            </TextField>
            {goodFilter && (
              <TextField
                label="Unit Filter"
                select
                size="small"
                value={unitFilter}
                onChange={(e) => setUnitFilter(e.target.value)}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="">All Units</MenuItem>
                {(() => {
                  const selectedGood = goods.find((g) => g._id === goodFilter);
                  const unit = selectedGood?.measurement_unit;
                  return unit
                    ? [
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>,
                      ]
                    : [];
                })()}
              </TextField>
            )}
          </Box>

          <TableContainer
            component={GlassPaper}
            sx={{ animation: `${floatIn} 500ms ease both` }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ref</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Good</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Comments</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((po, index) => (
                    <TableRow key={po.id || po._id || index} hover>
                      <TableCell>
                        #{po._id ? po._id.slice(0, 8) : po.id || index}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const effectiveGoodFilter =
                            goodFilter || globalGoodFilter;
                          return suppliers
                            .filter((supplier) =>
                              po.po_inv_items?.some(
                                (inv) =>
                                  inv.supplier === supplier._id &&
                                  (!effectiveGoodFilter ||
                                    inv.good === effectiveGoodFilter)
                              )
                            )
                            .map((item) => (
                              <div key={item._id}>{item.name || "N/A"}</div>
                            ));
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const effectiveGoodFilter =
                            goodFilter || globalGoodFilter;
                          return goods
                            .filter(
                              (good) =>
                                po.po_inv_items?.some(
                                  (inv) => inv.good === good._id
                                ) &&
                                (!effectiveGoodFilter ||
                                  good._id === effectiveGoodFilter)
                            )
                            .map((good) => (
                              <div key={good._id}>
                                - {good.name} ({good.measurement_unit})
                              </div>
                            ));
                        })()}
                      </TableCell>

                      <TableCell>
                        {po.received_date || po.orderDate || "N/A"}
                      </TableCell>
                      <TableCell>
                        AED{" "}
                        {po.total?.toFixed(2) || po.amount?.toFixed(2) || "N/A"}
                      </TableCell>
                      <TableCell>{po.comments || "N/A"}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <GradientButton
                            variant="contained"
                            size="small"
                            onClick={() => {
                              // setSelectedPO(po);
                              setOpenModal(true);
                              fetchById(po?._id, po?.storeId);
                              //console.log("ekhdem :: " + po);
                            }}
                          >
                            Details
                          </GradientButton>
                          <SecondaryButton
                            variant="outlined"
                            size="small"
                            startIcon={<PrintIcon />}
                            onClick={() => generateInvoicePDF(po, storeName)}
                            sx={{
                              minWidth: "auto",
                              px: 1,
                              "&:hover": {
                                background: "rgba(99,102,241,0.08)",
                                borderColor: "rgba(99,102,241,0.3)",
                              },
                            }}
                          >
                            PDF
                          </SecondaryButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredOrders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage(setPage)}
            onRowsPerPageChange={handleChangeRowsPerPage(
              setRowsPerPage,
              setPage
            )}
          />
        </>
      ) : (
        <Box mt={2} mb={2}>
          <StyledChip
            label="This table is not selected"
            sx={{ padding: 2, fontSize: 15 }}
            color="info"
          />
        </Box>
      )}

      <Box
        mt={2}
        sx={{ borderTop: "1px dashed rgba(99,102,241,0.06)", pt: 2 }}
      />
    </div>
  );

  return (
    <Root>
      <HeaderBar>
        <Box>
          <Title>Purchase Orders</Title>
          <SubTitle variant="body2">
            Monitoring & Visualisation — Restaurant Inventory
          </SubTitle>
        </Box>
      </HeaderBar>

      {/* ---- GLOBAL SUPPLIER FILTER ---- */}
      <Box
        sx={{
          background:
            "linear-gradient(90deg, rgba(6,182,212,0.08), rgba(99,102,241,0.08))",
          borderRadius: 2,
          p: 2,
          border: "1px solid rgba(99,102,241,0.12)",
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            Global Supplier Filter:
          </Typography>
          <TextField
            label="Filter All Tables by Supplier"
            select
            size="small"
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            sx={{
              minWidth: 280,
              "& .MuiOutlinedInput-root": {
                background: "rgba(255,255,255,0.8)",
              },
            }}
          >
            <MenuItem value="">All Suppliers (No Global Filter)</MenuItem>
            {suppliers.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          {supplierFilter && (
            <Typography variant="caption" color="text.secondary">
              Filtering all tables by:{" "}
              {suppliers.find((s) => s._id === supplierFilter)?.name}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ---- CARDS DES STORES ---- */}
      <StoreGrid>
        {[
          storeName0,
          storeName1,
          storeName2,
          storeName3,
          storeName4,
          storeName5,
          storeName6,
          storeName7,
        ].map((name, idx) => (
          <StoreCard key={idx} active={selectedStores.includes(idx)}>
            <CardActionArea onClick={() => toggleStore(idx)} sx={{ p: 2 }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 80,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, textAlign: "center" }}
                >
                  {name}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {selectedStores.includes(idx)
                    ? "Monitoring active"
                    : "Tap to view"}
                </Typography>
              </CardContent>
            </CardActionArea>
          </StoreCard>
        ))}
      </StoreGrid>

      {renderTable(
        storeName0,
        total,
        filteredPurchaseOrders0,
        filteredTotal0,
        showTable0,
        setShowTable0,
        page0,
        setPage0,
        rowsPerPage0,
        setRowsPerPage0,
        searchTerm0,
        setSearchTerm0,
        filterFrom0,
        setFilterFrom0,
        filterTo0,
        setFilterTo0,
        supplierFilter0,
        setSupplierFilter0,
        goodFilter0,
        setGoodFilter0,
        unitFilter0,
        setUnitFilter0,
        0,
        globalGoodFilter,
        globalUnitFilter
      )}
      {renderTable(
        storeName1,
        total1,
        filteredPurchaseOrders1,
        filteredTotal1,
        showTable1,
        setShowTable1,
        page1,
        setPage1,
        rowsPerPage1,
        setRowsPerPage1,
        searchTerm1,
        setSearchTerm1,
        filterFrom1,
        setFilterFrom1,
        filterTo1,
        setFilterTo1,
        supplierFilter1,
        setSupplierFilter1,
        goodFilter1,
        setGoodFilter1,
        unitFilter1,
        setUnitFilter1,
        1,
        globalGoodFilter,
        globalUnitFilter
      )}
      {renderTable(
        storeName2,
        total2,
        filteredPurchaseOrders2,
        filteredTotal2,
        showTable2,
        setShowTable2,
        page2,
        setPage2,
        rowsPerPage2,
        setRowsPerPage2,
        searchTerm2,
        setSearchTerm2,
        filterFrom2,
        setFilterFrom2,
        filterTo2,
        setFilterTo2,
        supplierFilter2,
        setSupplierFilter2,
        goodFilter2,
        setGoodFilter2,
        unitFilter2,
        setUnitFilter2,
        2,
        globalGoodFilter,
        globalUnitFilter
      )}

      {renderTable(
        storeName3,
        total3,
        filteredPurchaseOrders3,
        filteredTotal3,
        showTable3,
        setShowTable3,
        page3,
        setPage3,
        rowsPerPage3,
        setRowsPerPage3,
        searchTerm3,
        setSearchTerm3,
        filterFrom3,
        setFilterFrom3,
        filterTo3,
        setFilterTo3,
        supplierFilter3,
        setSupplierFilter3,
        goodFilter3,
        setGoodFilter3,
        unitFilter3,
        setUnitFilter3,
        3,
        globalGoodFilter,
        globalUnitFilter
      )}

      {renderTable(
        storeName4,
        total4,
        filteredPurchaseOrders4,
        filteredTotal4,
        showTable4,
        setShowTable4,
        page4,
        setPage4,
        rowsPerPage4,
        setRowsPerPage4,
        searchTerm4,
        setSearchTerm4,
        filterFrom4,
        setFilterFrom4,
        filterTo4,
        setFilterTo4,
        supplierFilter4,
        setSupplierFilter4,
        goodFilter4,
        setGoodFilter4,
        unitFilter4,
        setUnitFilter4,
        4,
        globalGoodFilter,
        globalUnitFilter
      )}

      {renderTable(
        storeName5,
        total5,
        filteredPurchaseOrders5,
        filteredTotal5,
        showTable5,
        setShowTable5,
        page5,
        setPage5,
        rowsPerPage5,
        setRowsPerPage5,
        searchTerm5,
        setSearchTerm5,
        filterFrom5,
        setFilterFrom5,
        filterTo5,
        setFilterTo5,
        supplierFilter5,
        setSupplierFilter5,
        goodFilter5,
        setGoodFilter5,
        unitFilter5,
        setUnitFilter5,
        5,
        globalGoodFilter,
        globalUnitFilter
      )}

      {renderTable(
        storeName6,
        total6,
        filteredPurchaseOrders6,
        filteredTotal6,
        showTable6,
        setShowTable6,
        page6,
        setPage6,
        rowsPerPage6,
        setRowsPerPage6,
        searchTerm6,
        setSearchTerm6,
        filterFrom6,
        setFilterFrom6,
        filterTo6,
        setFilterTo6,
        supplierFilter6,
        setSupplierFilter6,
        goodFilter6,
        setGoodFilter6,
        unitFilter6,
        setUnitFilter6,
        6,
        globalGoodFilter,
        globalUnitFilter
      )}

      {renderTable(
        storeName7,
        total7,
        filteredPurchaseOrders7,
        filteredTotal7,
        showTable7,
        setShowTable7,
        page7,
        setPage7,
        rowsPerPage7,
        setRowsPerPage7,
        searchTerm7,
        setSearchTerm7,
        filterFrom7,
        setFilterFrom7,
        filterTo7,
        setFilterTo7,
        supplierFilter7,
        setSupplierFilter7,
        goodFilter7,
        setGoodFilter7,
        unitFilter7,
        setUnitFilter7,
        7,
        globalGoodFilter,
        globalUnitFilter
      )}

      {/* ---- MODAL PO DETAILS ---- */}
      {fetchedPO && (
        <Dialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { width: "90%", maxWidth: "1200px", borderRadius: 2 },
          }}
        >
          <DialogTitle
            sx={{
              background:
                "linear-gradient(90deg, rgba(6,182,212,0.06), rgba(99,102,241,0.06))",
            }}
          >
            Purchase Order Details
          </DialogTitle>
          <DialogContent>
            {(() => {
              const poData = fetchedPO;
              const invoiceNos = poData.invoice_no
                ? poData.invoice_no.split("\\") && poData.invoice_no?.split("/")
                : [];
              return (
                <>
                  {modalLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Typography sx={{ fontWeight: 700, mb: 1 }}>
                        Order Number: #{poData.order_no || "N/A"}
                      </Typography>
                      <Typography>
                        Supplier:{" "}
                        {suppliers.find(
                          (s) => s._id === poData.po_inv_items?.[0]?.supplier
                        )?.name || "N/A"}
                      </Typography>
                      <Typography>
                        Date:{" "}
                        {poData.received_date || poData.orderDate || "N/A"}
                      </Typography>

                      <Typography>Invoice No: {poData.invoice_no}</Typography>
                      <Typography sx={{ mb: 2 }}>
                        Comments: {poData.comments || "N/A"}
                      </Typography>

                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        Goods
                      </Typography>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Good</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Quantity</TableCell>
                            <TableCell>Buying Unit</TableCell>

                            <TableCell>Unit</TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell>Tax</TableCell>
                            <TableCell>Total w/ Tax</TableCell>
                            <TableCell>Invoice No</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {goods
                            .filter((good) =>
                              poData.po_inv_items?.some(
                                (inv) => inv.good === good._id
                              )
                            )
                            .map((good, index) => {
                              const poItem = poData.po_inv_items.find(
                                (inv) => inv.good === good._id
                              );
                              const qty = Number(poItem?.quantity) || 0;

                              return (
                                <TableRow key={good._id}>
                                  <TableCell>{good.name || "N/A"}</TableCell>

                                  <TableCell>
                                    {categories
                                      .filter(
                                        (cat) => cat._id === good.category
                                      )
                                      .map((cat) => (
                                        <div key={cat._id}>{cat.name}</div>
                                      ))}
                                  </TableCell>

                                  <TableCell>
                                    {poItem?.price_used?.toFixed(2) || "N/A"}
                                  </TableCell>
                                  <TableCell>{qty}</TableCell>

                                  <TableCell>
                                    {good.goods_suppliers[0]?.buying_unit ||
                                      "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {good.measurement_unit || "N/A"}
                                  </TableCell>

                                  {/* Total HT */}
                                  <TableCell>
                                    {((poItem?.price_used || 0) * qty).toFixed(
                                      2
                                    )}
                                  </TableCell>

                                  {/* Total Tax */}
                                  <TableCell>
                                    {((poItem?.tax_used || 0) * qty).toFixed(2)}
                                  </TableCell>

                                  {/* Total TTC = HT + Tax */}
                                  <TableCell>
                                    {(
                                      ((poItem?.price_used || 0) +
                                        (poItem?.tax_used || 0)) *
                                      qty
                                    ).toFixed(2)}
                                  </TableCell>

                                  <TableCell>
                                    {invoiceNos[index] || invoiceNos}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>

                        <TableFooter>
                          {(() => {
                            const { subtotal, totalTax } = goods
                              .filter((good) =>
                                poData.po_inv_items?.some(
                                  (inv) => inv.good === good._id
                                )
                              )
                              .reduce(
                                (acc, good) => {
                                  const poItem = poData.po_inv_items.find(
                                    (inv) => inv.good === good._id
                                  );
                                  const qty = Number(poItem?.quantity) || 0;
                                  const price = Number(poItem?.price_used) || 0;
                                  const tax = Number(poItem?.tax_used) || 0;

                                  const lineSubtotal = price * qty;
                                  const lineTax = tax * qty;

                                  acc.subtotal += lineSubtotal;
                                  acc.totalTax += lineTax;

                                  return acc;
                                },
                                { subtotal: 0, totalTax: 0 }
                              );

                            return (
                              <TableRow>
                                <TableCell
                                  colSpan={6}
                                  style={{
                                    fontWeight: "bold",
                                    textAlign: "right",
                                  }}
                                >
                                  Totals :
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  {subtotal.toFixed(2)}
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  {totalTax.toFixed(2)}
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  {(subtotal + totalTax).toFixed(2)}
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            );
                          })()}
                        </TableFooter>
                      </Table>
                    </>
                  )}
                </>
              );
            })()}
          </DialogContent>

          <DialogActions>
            {/* <SecondaryButton
              variant='outlined'
              startIcon={<PrintIcon />}
              onClick={() =>
                generateInvoicePDF(
                  fetchedPO,
                  getStoreNameForPO(fetchedPO)
                )
              }
              sx={{
                mr: 1,
                "&:hover": {
                  background: "rgba(99,102,241,0.08)",
                  borderColor: "rgba(99,102,241,0.3)",
                },
              }}>
              Print Invoice
            </SecondaryButton> */}
            <Button onClick={() => setOpenModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Root>
  );
};

const PurchaseOrders = () => {
  return (
    <PurchaseOrdersProvider
      endpoint0={`${baseUrl}/api/purchase-orders/purchase-orders-api-0`}
      endpoint1={`${baseUrl}/api/purchase-orders/purchase-orders-api-1`}
      endpoint2={`${baseUrl}/api/purchase-orders/purchase-orders-api-2`}
      endpoint3={`${baseUrl}/api/purchase-orders/purchase-orders-api-3`}
      endpoint4={`${baseUrl}/api/purchase-orders/purchase-orders-api-4`}
      endpoint5={`${baseUrl}/api/purchase-orders/purchase-orders-api-5`}
      endpoint6={`${baseUrl}/api/purchase-orders/purchase-orders-api-6`}
      endpoint7={`${baseUrl}/api/purchase-orders/purchase-orders-api-7`}
    >
      <PurchaseOrdersTable />
    </PurchaseOrdersProvider>
  );
};

export default PurchaseOrders;
